import { FormikProps } from 'formik';
import { ReactElement } from 'react';
import * as yup from 'yup';
import {
  PlanEntitlementKey,
  Scratchpad,
} from '../../../../api/graphql/generated';
import { RolePermissionInputType } from '../../../../components/role/role.types';
import { IntercomArticle } from '../../../../config/intercom';
import { i18nKeyType } from '../../../../i18n';
import { Waypoint } from '../../../../util/machine-variable.utils';
import {
  FineTunedPointRequired,
  WorkPiecePosition,
} from '../../../../util/part.util';

export enum FlowState {
  DEFAULT = 'default',
  EDIT = 'edit',
  TEACH = 'teach',
}

export enum FlowModal {
  PART_DETAILS,
  PART_HISTORY,
  APPLICATION_SETTINGS,
}

export enum FlowPanel {
  TEACH = 'teach',
  EDIT = 'edit',
  MULTI_EDIT = 'multi_edit',
}

export enum SystemUnit {
  IMPERIAL = 'imperial',
  METRIC = 'metric',
}

export type ToolEquipment = string;
export type FlowItemRef = string;
export type FlowItemParameterRef = string;

export interface FlowItemParameterAdjustmentRange {
  minimum: number;
  maximum: number;
}

export interface FlowItemParameterAdjustmentMetadata {
  value?: number;
  type: RolePermissionInputType.PERCENTAGE | RolePermissionInputType.NUMBER;
}

export type FlowItemParameterRefAdjustmentMetadata<
  Ref extends string = FlowItemParameterRef,
> = Partial<Record<Ref, FlowItemParameterAdjustmentMetadata>>;

export interface ProgramNameMapping {
  [programName: string]: i18nKeyType;
}
export interface TeachIOMapping {
  input: {
    capture: string;
  };
  output: {
    light: string;
  };
}

export interface ToolConfig {
  name: string;
  tcp: Waypoint;
  cog: [number, number, number];
  mass: number;
  inertia: [number, number, number, number, number, number];
}

export interface Tool {
  id: string;
  label: string;
  config: ToolConfig;
  equipment: ToolEquipment[];
  models: string[]; // robot models that support this tool (e.g. UR5, UR10, etc.)
}

export interface Flow<FlowConfig = Record<string, any>> {
  config: FlowConfig;
  items: FlowItem[];
  defaultParameterSet?: ApplicationDefaultParameterSet;
  /**
   * TODO - remove when all clients are on shared code https://github.com/Hirebotics/beacon/pull/1388
   * @deprecated use defaultParameterSet instead
   */
  weldParameterSet?: ApplicationDefaultParameterSet;
  recommendation?: {
    id: string;
    utilized: boolean;
  };
}

export enum WorkAngleType {
  FIXED = 'fixed',
  RELATIVE = 'relative',
}

export type FlowItemAttributes = Record<string, unknown> & {
  parentSectionTempId?: string;
  workSurfacePosition?: WorkPiecePosition;
  workSurfaceOffset?: number;
};

export interface FlowItem<
  RefType = FlowItemRef,
  AttributesType extends FlowItemAttributes = FlowItemAttributes,
> {
  id?: string;
  name?: string;
  ref: RefType;
  parameters: FlowItemParameter[];
  attributes?: AttributesType;
}

export enum FlowPlaceholderType {
  'DROP',
  'START',
  'MIDDLE',
}

export enum FlowItemSharedRef {
  END = 'end',
  MOVE = 'move',
  MOVE_HOME = 'move to home',
  PRODUCTION_REPORTING = 'report production',
  SECTION = 'section',
  START = 'start',
  TRIGGER_OUTPUT = 'trigger output',
  WAIT_FOR = 'wait for operator',
}

export enum FlowItemSharedParameterRef {
  QNEAR = 'qnear',
  WAIT_FOR_TYPE_OF_INPUT = 'typeOfInput',
  WAYPOINT = 'waypoint',
  SECTION_COLOR = 'sectionColor',
  STICKOUT = 'stickOut',
}

export enum FlowItemSharedParameterTag {
  WAIT_FOR_INPUT = 'waitForInput',
  WAIT_FOR_TIME = 'waitForTime',
  WAIT_FOR_OPERATOR = 'waitForOperator',
}

export interface FlowHelperItem {
  identifier: 'placeholder' | 'sectionEnd';
}

export interface FlowPlaceholder extends FlowHelperItem {
  identifier: 'placeholder';
  type: FlowPlaceholderType;
}

export interface FlowSectionEnd extends FlowHelperItem {
  identifier: 'sectionEnd';
  sectionId: string;
}

export type FlowItemArrayType = FlowItem | FlowHelperItem;

export interface FlowItemSectionAttributes<SectionType extends string>
  extends FlowItemAttributes {
  type?: SectionType;
  endItemId?: string;
  isParent?: boolean;
  tempId?: string;
}

export type FlowItemSection<SectionType extends string = string> = FlowItem<
  string,
  FlowItemSectionAttributes<SectionType>
>;

// Though an endItemId may be expected, it cannot be required since our FlowItem
// interface doesn't guarantee an id.
export type FlowItemSectionParentAttributes<SectionType extends string> =
  FlowItemSectionAttributes<SectionType> & {
    isParent: true;
    type: FlowItemSectionAttributes<SectionType>['type'];
    originalItems: FlowItemArrayType[];
    creationIndex: number;
  };

export type UnsavedFlowItemSectionParentAttributes<SectionType extends string> =
  FlowItemSectionParentAttributes<SectionType> & {
    tempId: string;
  };

export type FlowItemSectionParent<SectionType extends string = string> =
  FlowItem & {
    id: string;
    attributes: FlowItemSectionParentAttributes<SectionType>;
  };

export type UnsavedFlowItemSectionParent<SectionType extends string = string> =
  FlowItem & {
    attributes: UnsavedFlowItemSectionParentAttributes<SectionType>;
  };

export interface FlowSectionItem {
  index: number;
  item: FlowItemArrayType;
}

export type UnsavedNestedFlowItemAttributes = {
  parentSectionTempId: string;
};

export type UnsavedNestedFlowItem = FlowItem & {
  attributes: UnsavedNestedFlowItemAttributes;
};

export interface FlowSection<SectionType extends string = string> {
  section?: {
    index: number;
    item: FlowItem<FlowItemRef, FlowItemSectionAttributes<SectionType>>;
    parentItem?:
      | FlowItemSectionParent<SectionType>
      | UnsavedFlowItemSectionParent<SectionType>;
  };
  items: FlowSectionItem[];
}

export interface FlowSectionParent<SectionType extends string = string> {
  section?: {
    index: number;
    item:
      | FlowItemSectionParent<SectionType>
      | UnsavedFlowItemSectionParent<SectionType>;
    parentItem?:
      | FlowItemSectionParent<SectionType>
      | UnsavedFlowItemSectionParent<SectionType>;
  };
  items: FlowSectionItem[];
}

export interface FlowItemParameter<T = any> {
  ref: FlowItemParameterRef;
  value: T;
  baseValue: T; // set by manager or editor
}

export interface ParametersFormValue<T = unknown> {
  value: T;
  originalValue: T;
  baseValue: T;
  originalBaseValue: T; // set by manager or editor
}

export interface ParametersForm {
  [ref: string]: ParametersFormValue;
}

export interface FlowItemForm extends Omit<FlowItem, 'parameters'> {
  parameters: ParametersForm;
  [key: string]: any;
}

export interface FlowItemMetadata {
  ref: FlowItemRef;
  codeBlock: number;
  name: i18nKeyType;
  description: i18nKeyType;
  icon: string;
  displayOrder?: number;
  availableParameters: FlowItemParameterMetadata[];
  articleId?: IntercomArticle;
  hidden?: boolean;
}

export type ParameterValueType =
  | 'number'
  | 'string'
  | 'boolean'
  | 'array'
  | 'object';

export interface PropertyValueUOM {
  precision?: number;
  system?: {
    imperial: string;
    metric: string;
  };
  target: string;
}

export interface ParameterValue<T = any> {
  type: ParameterValueType;
  minimum?: number;
  maximum?: number;
  default?: T;
  required?: boolean;
  uom?: PropertyValueUOM;
  props?: Record<string, ParameterValue>;
}

export interface BuildParameterValidationContext<FlowConfig = any> {
  flow: Flow<FlowConfig>;
  form: ParametersForm;
  value: ParameterValue;
  range?: FlowItemParameterAdjustmentRange;
}

export interface FlowItemParameterMetadata<TagType = any, Config = any> {
  ref: FlowItemParameterRef;
  transient?: boolean;
  tags?: TagType[];
  tagCondition?: (tags: TagType[], form: ParametersForm) => boolean;
  apiName: string;
  name?: i18nKeyType;
  displayOrder?: number;
  description?: i18nKeyType;
  ui?: ParameterUI;
  value: ParameterValue;
  recommendation?: string;
  calculateValue?: (
    config: Config,
    item: FlowItem,
    currentValue: any,
    flowItems: FlowItem[]
  ) => any;
  buildValidation?: (
    context: BuildParameterValidationContext
  ) => ParameterValue;
  readonlyCondition?: (form: ParametersForm) => boolean;
  readonlyHint?: i18nKeyType;
  featureFlag?: string;
  paywallOptions?: {
    planEntitlementKey?: PlanEntitlementKey;
  };
}

export type ParameterUI =
  | HiddenWidget
  | NumberWidget
  | PickerWidget
  | SliderWidget
  | SwitchWidget
  | TextWidget
  | ColorPickerWidget;

interface HiddenWidget {
  widget: WidgetType.HIDDEN;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widgetOptions?: any;
}
interface NumberWidget {
  widget: WidgetType.NUMBER;
  widgetOptions?: {
    step?: number;
  };
}

export interface PickerWidget {
  widget: WidgetType.PICKER;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widgetOptions: Array<{
    label: i18nKeyType;
    value: boolean | string | number | Record<string, unknown>;
  }>;
}

interface SwitchWidget {
  widget: WidgetType.SWITCH;
  widgetOptions?: never;
}

interface SliderWidget {
  widget: WidgetType.SLIDER;
  widgetOptions: {
    step: number;
  };
}

interface TextWidget {
  widget: WidgetType.TEXT;
  widgetOptions?: never;
}

interface ColorPickerWidget {
  widget: WidgetType.COLOR_PICKER;
  widgetOptions?: never;
}

export enum WidgetType {
  HIDDEN = 'hidden',
  NUMBER = 'number',
  OBJECT = 'object',
  OFFSET = 'offset',
  PICKER = 'picker',
  SLIDER = 'slider',
  SWITCH = 'switch',
  WAYPOINT = 'waypoint',
  TEXT = 'text',
  COLOR_PICKER = 'colorPicker',
}

export type ApplicationDefaultParameterSetData = {
  parameters: FlowItemParameter[];
};

export type ApplicationDefaultParameterSet = {
  id?: string;
  version?: string;
  data: ApplicationDefaultParameterSetData;
};

export interface ApplicationDefaultParameterSetFormOptionsPreferenceValue {
  default: ApplicationDefaultParameterSetData;
  [equipment: string]: ApplicationDefaultParameterSetData;
}

export type ApplicationDefaultParameterSetTagToEquipmentMapping = Record<
  string,
  ApplicationDefaultParameterSetTagData
>;

export type ApplicationDefaultParameterSetTags = Record<string, unknown>;

export type ApplicationDefaultParameterSetTagOptionValue =
  | string
  | number
  | Record<string, unknown>;

export interface ApplicationDefaultParameterSetTagFilter {
  tag: string;
  filter: (string | number)[];
}

export interface ApplicationDefaultParameterSetTagOption {
  label: i18nKeyType;
  value: ApplicationDefaultParameterSetTagOptionValue;
  searchTerms?: string[];
  id?: string;
  filters?: ApplicationDefaultParameterSetTagFilter[][];
}

export interface ApplicationDefaultParameterSetTag {
  label: i18nKeyType;
  options: ApplicationDefaultParameterSetTagOption[];
  order?: number;
  required?: boolean;
  dependency?: string;
}

export interface ApplicationDefaultParameterSetTagData {
  [key: string]: ApplicationDefaultParameterSetTag;
}

export interface ApplicationDefaultParameterSetTagEquipmentMap {
  [equipment: string]: ApplicationDefaultParameterSetTagData;
}

export type ApplicationDefaultParameterSetFormTags = Record<
  string,
  string | number
>;

export interface RuleValidationResponse {
  message?: string;
  level: RuleResponseLevel;
  startIndex: number;
  endIndex: number;
  startItemId: string;
  endItemId: string;
}

export type RuleValidation = (flow: Flow) => RuleValidationResponse | undefined;

export enum RuleResponseLevel {
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export interface FlowSaveResult {
  scratchpad: Scratchpad | undefined;
  errors: FlowValidateResponse;
}

export interface FlowRule {
  execute: RuleValidation;
}

export type FlowValidateResponse = FlowValidateError[] | undefined;
export type FlowValidateError = RuleValidationResponse;

export interface FlowItemYupSchemaOptions {
  parameters?: FlowItemParameterMetadata[];
  systemUnit?: SystemUnit;
}

export interface FlowItemText {
  itemId: string;
  index: string;
  name: string;
  label: string;
}

export const WaypointParameterValue: ParameterValue = {
  type: 'object',
  required: true,
  props: {
    x: {
      type: 'number',
      required: true,
    },
    y: {
      type: 'number',
      required: true,
    },
    z: {
      type: 'number',
      required: true,
    },
    rx: {
      type: 'number',
      required: true,
    },
    ry: {
      type: 'number',
      required: true,
    },
    rz: {
      type: 'number',
      required: true,
    },
  },
};

export interface FlowSelectedItemStats {
  count: number;
  selected: number;
}

export type FlowSelectedItemInfo = Record<string, FlowSelectedItemStats>;

export enum ApplicationClipboardPath {
  FLOW_ITEMS = 'flow_items',
}

export enum WaitForInputType {
  OPERATOR_BUTTON = 'OPERATOR_BUTTON',
  SENSOR = 'SENSOR',
  TIME = 'TIME',
}

export type ApplicationDefaultParameterSetBaseFormChild<T> = (props: {
  formikProps: FormikProps<T>;
  disabled?: boolean;
}) => ReactElement;

export type ApplicationDefaultParameterSetBaseFormValidationBuilder = (
  dataSchema: yup.AnySchema<unknown, unknown, unknown> | undefined
) => yup.AnyObjectSchema;

export interface ApplicationDefaultParameterSetBaseFormProps<T> {
  buildValidationSchema: ApplicationDefaultParameterSetBaseFormValidationBuilder;
  excludeTags?: string[];
  footer: ApplicationDefaultParameterSetBaseFormChild<T>;
  formValues: T;
  onSubmit: (form: T) => Promise<void>;
  postForm?: ApplicationDefaultParameterSetBaseFormChild<T>;
  preForm?: ApplicationDefaultParameterSetBaseFormChild<T>;
  tagsReadOnly?: boolean;
  paramsReadOnly?: boolean;
  system: SystemUnit;
}

export interface FineTuneSettings {
  travelAngle?: number;
  workAngle?: number;
  stickout?: number;
}

export interface FineTuneProps {
  supported: boolean;
  type: 'linear' | 'radial';
  pointToProcess: FineTunedPointRequired;
  startPoint: Waypoint;
  midPoint?: Waypoint;
  endPoint?: Waypoint;
  workPiecePosition?: WorkPiecePosition;
  workPieceOffset?: number;
  toolConfig: ToolConfig;
  stickout: number;
  poseIncludesStickout: boolean;
  ui: {
    travelAngle: {
      label: i18nKeyType;
    };
    workAngle: {
      label: i18nKeyType;
    };
    path: {
      label: i18nKeyType;
    };
    stickout: {
      label: i18nKeyType;
    };
    applyTo: {
      label: i18nKeyType;
    };
  };
  executingIndex: number;
}

export interface FineTuneAngles {
  travelAngle?: number;
  workAngle?: number;
}
